/**
 * The idea page's checklist (DD-013, DD-052): the idea's own tasks, then a
 * via group per related item (its brief, posts and events) other tasks came
 * through. One task drawer; "+ Add task" creates a task linked to the
 * idea. Admin only: the portal idea drawer never mounts it.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { TaskDrawer, TaskNavOptions } from "../drawer/TaskDrawer";
import { viaItemPath } from "../drawer/taskLinks";
import { useTaskDrawer } from "../drawer/useTaskDrawer";
import { useIdeaTasksQuery, useToggleTask } from "../hooks/useTasks";
import { capabilitiesFor } from "../taskCapabilities";
import { taskListProgress } from "../taskList";
import { EMPTY_TASK_LIST } from "../types";
import { TaskListEditor } from "./TaskListEditor";

interface IdeaTasksSectionProps {
  ideaId: string;
  clientId: string;
  /** The idea page's trail options, for links followed from the drawer. */
  navOptions?: TaskNavOptions;
}

const ADMIN = capabilitiesFor("admin");

export function IdeaTasksSection({ ideaId, clientId, navOptions }: IdeaTasksSectionProps) {
  const { data: list = EMPTY_TASK_LIST } = useIdeaTasksQuery(ideaId);
  const toggleTask = useToggleTask();
  const drawer = useTaskDrawer();
  const navigate = useNavigate();
  const { done, total } = taskListProgress(list);

  return (
    <div>
      <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
        {ENTITY_CONFIG.task.plural}
        {total > 0 && (
          <span className="ml-2 normal-case tracking-normal text-gray-400 font-normal">
            {done}/{total} done
          </span>
        )}
      </div>
      <TaskListEditor
        list={list}
        capabilities={ADMIN}
        onToggle={toggleTask}
        onOpen={drawer.open}
        onAdd={() => drawer.create({ clientId, links: [{ type: "idea", targetId: ideaId }] })}
        onOpenItem={(item) => navigate(viaItemPath(item, clientId), navOptions)}
        emptyHint="Break the idea into shots, assets and prep"
      />
      <TaskDrawer drawer={drawer} capabilities={ADMIN} navOptions={navOptions} />
    </div>
  );
}
